import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

import { SearchService } from '../../../core/services/search.service';
import { UsersService } from '../../../core/services/users.service';
import { environment } from '../../../../environments/environment';

// The search next to the logo in the topbar, as in cinemapp.be's header: a title, a member, a theater, a cast member
// or a streaming service, opening its admin page. Two searches side by side: the site's search index (every type,
// ranked) and the admin's own user search (name, username or e-mail, any part, hidden accounts included - the index
// has neither). The groups come in the index's order, the type of its best hit first; a list has no admin page and
// is left out.

interface SearchResult {
    index: number; // place in the dropdown as a whole, what the arrow keys walk
    type: string;
    route: any[];
    name: string;
    detail?: string;
    badge?: string;
    image?: string;
}

interface SearchGroup {
    type: string;
    label: string;
    results: SearchResult[];
}

const GROUP_LABELS = {
    title: 'Titles',
    user: 'Users',
    theater: 'Theaters',
    cast: 'Cast',
    'watch-now-service': 'Streaming services'
};
const PER_GROUP = 5;
const MIN_LENGTH = 2; // users/admin/search answers nothing below it

@Component({
    selector: 'app-topbar-search',
    templateUrl: './topbar-search.component.html',
    styleUrls: ['./topbar-search.component.scss']
})
export class TopbarSearchComponent implements OnInit, OnDestroy {

    @ViewChild('input') input: ElementRef<HTMLInputElement>;

    query = '';
    groups: SearchGroup[] = [];
    results: SearchResult[] = []; // the groups' results in one list
    active = -1;
    open = false;
    loading = false;

    private answeredQuery = ''; // the query `groups` belongs to
    private openFirstWhenAnswered = false; // Enter before the results of what was typed were in
    private queries = new Subject<string>();
    private subscription: Subscription;
    private cdn = environment.cdn;

    constructor(private router: Router, private searchService: SearchService, private usersService: UsersService) { }

    ngOnInit() {
        this.subscription = this.queries.pipe(
            debounceTime(250),
            switchMap(query => {
                if (query.length < MIN_LENGTH) return of({ query, groups: [] });
                return forkJoin([
                    this.searchService.global(query).pipe(catchError(() => of([]))),
                    this.usersService.search(query).pipe(catchError(() => of([])))
                ]).pipe(map(([hits, users]) => ({ query, groups: this.toGroups(hits, users) })));
            })
        ).subscribe(({ query, groups }) => this.answered(query, groups));
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    // "/" anywhere outside a text field goes to the search, as on GitHub
    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent) {
        if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
        const target = event.target as HTMLElement;
        if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
        event.preventDefault();
        this.input.nativeElement.focus();
    }

    didType(value: string) {
        this.query = value;
        this.active = -1;
        this.openFirstWhenAnswered = false;
        const query = value.trim();
        this.loading = query.length >= MIN_LENGTH && query !== this.answeredQuery;
        this.open = query.length >= MIN_LENGTH;
        this.queries.next(query);
    }

    didFocus() {
        this.open = this.query.trim().length >= MIN_LENGTH;
    }

    didBlur() {
        this.open = false;
        this.active = -1;
    }

    onKeydown(event: KeyboardEvent) {
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                if (!this.open || !this.results.length) return;
                event.preventDefault(); // the caret stays where it is
                const step = event.key === 'ArrowDown' ? 1 : -1;
                this.active = Math.max(-1, Math.min(this.results.length - 1, this.active + step));
                this.scrollToActive();
                break;
            case 'Enter':
                // The highlighted result, or the first one. Typed faster than the search: the first of what comes back.
                if (!this.open) return;
                if (this.loading && this.active < 0) {
                    this.openFirstWhenAnswered = true;
                    return;
                }
                const result = this.results[Math.max(this.active, 0)];
                if (!result) return;
                if (event.ctrlKey || event.metaKey) { // a new tab, the results stay
                    window.open(this.router.serializeUrl(this.router.createUrlTree(result.route)), '_blank');
                    return;
                }
                this.router.navigate(result.route);
                this.reset();
                break;
            case 'Escape': // closes, the text stays: the next focus shows its results again
                this.input.nativeElement.blur();
                break;
        }
    }

    // A click on a result: routerLink navigates. A ctrl/cmd/shift click opens a new tab and leaves the results open.
    didClick(event: MouseEvent) {
        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) this.reset();
    }

    trackByType(index: number, group: SearchGroup) {
        return group.type;
    }

    private answered(query: string, groups: SearchGroup[]) {
        this.groups = groups;
        this.results = [].concat(...groups.map(group => group.results));
        this.answeredQuery = query;
        this.active = -1;
        if (query !== this.query.trim()) return; // typed on since: the results of what is in the box are on their way

        this.loading = false;
        if (this.openFirstWhenAnswered) {
            this.openFirstWhenAnswered = false;
            if (this.results.length) {
                this.router.navigate(this.results[0].route);
                this.reset();
            }
        }
    }

    // The box empties after a result was opened, so the next search starts clean
    private reset() {
        this.query = '';
        this.input.nativeElement.value = '';
        this.queries.next('');
        this.groups = [];
        this.results = [];
        this.answeredQuery = '';
        this.loading = false;
        this.openFirstWhenAnswered = false;
        this.input.nativeElement.blur();
        this.didBlur();
    }

    private scrollToActive() {
        setTimeout(() => document.getElementById(`topbar-search-result-${this.active}`)?.scrollIntoView({ block: 'nearest' }));
    }

    private toGroups(hits: any[], users: any[]): SearchGroup[] {
        const byType = new Map<string, any[]>(); // in the order of each type's best hit
        const add = (type: string, item: any) => {
            if (!byType.has(type)) byType.set(type, []);
            byType.get(type).push(item);
        };

        // A user the index found is shown with what the admin search knows of it (the e-mail, a hidden account);
        // the users only the admin search found come after the index's
        const adminUsers = new Map((users || []).map(user => [`${user._id}`, user]));
        const seenUsers = new Set<string>();
        for (const hit of hits || []) {
            if (!GROUP_LABELS[hit.type]) continue;
            if (hit.type === 'user') {
                seenUsers.add(`${hit._id}`);
                add('user', adminUsers.get(`${hit._id}`) || hit);
            } else {
                add(hit.type, hit);
            }
        }
        for (const user of users || []) {
            if (!seenUsers.has(`${user._id}`)) add('user', user);
        }

        let index = 0;
        const groups: SearchGroup[] = [];
        byType.forEach((items, type) => {
            const results = items.map(item => this.toResult(type, item)).filter(Boolean).slice(0, PER_GROUP);
            results.forEach(result => result.index = index++);
            if (results.length) groups.push({ type, label: GROUP_LABELS[type], results });
        });
        return groups;
    }

    private toResult(type: string, item: any): SearchResult {
        const image = (picture: string, size: string) => picture ? `${this.cdn}${picture}${size}.webp` : undefined;
        const details = (...parts: any[]) => parts.filter(part => part || part === 0).join(' · ');

        switch (type) {
            case 'title':
                if (!item.cid) return null;
                return {
                    index: 0, type, route: ['/titles', item.cid],
                    name: item.name,
                    detail: details(item.title !== item.name ? item.title : null, item.isTvShow ? 'Series' : 'Movie', item.releaseYear),
                    image: image(item.poster, '_400')
                };
            case 'user':
                return {
                    index: 0, type, route: ['/users', item._id],
                    name: item.name || item.username,
                    detail: details(item.username ? `@${item.username}` : null, item.email),
                    badge: item.settings?.accountType === 'hidden' ? 'Hidden' : item.authentication?.isAdmin ? 'Admin' : undefined,
                    image: image(item.image, '_100')
                };
            case 'theater':
                return {
                    index: 0, type, route: ['/theaters', item._id],
                    name: item.name,
                    detail: details(item.alternatives?.[0], item.country?.toUpperCase()),
                    image: image(item.icon, '_100')
                };
            case 'cast':
                return { index: 0, type, route: ['/cast', item._id], name: item.name };
            case 'watch-now-service':
                return { index: 0, type, route: ['/watch-now-services', item._id], name: item.name, image: image(item.icon, '_200') };
        }
        return null;
    }
}
